import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Collapse, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  PRIMITIVE_TYPES,
  PRIMITIVE_LABELS,
  PRIMITIVE_COLORS,
  DRAG_MIME_PRIMITIVE,
  DRAG_MIME_ACTION,
  type PrimitiveType,
  type CatalogAction,
  type CatalogActionsByCategory,
  type CatalogConnection,
} from './workflowGraphTypes';

// Temporary — hides the "Advanced: raw primitives" section of the palette. Flip back to false
// to unhide it; the section's rendering/behavior below is untouched, only this flag gates it
// (same on/off convention as WORKFLOW_TAB_HIDDEN in channelSettingsTabs.ts).
const RAW_PRIMITIVES_SECTION_HIDDEN = true;

/**
 * Native HTML5 drag only fires reliably from a plain element — dragging directly off a
 * MUI `Button` (a `<button>`) is inconsistent across browsers. This wraps a button-like child
 * in a draggable `<div>` so the visual tile is unchanged but the drag gesture is reliable.
 * `effectAllowed` must match the drop target's `dropEffect` ('move' on both, see CanvasSurface
 * in WorkflowCanvasTab.tsx) or some browsers silently refuse the drop.
 */
const DraggableTile: React.FC<{ onDragStart: (e: React.DragEvent<HTMLDivElement>) => void; sx?: SxProps<Theme>; children: React.ReactNode }> = ({
  onDragStart,
  sx,
  children,
}) => (
  <Box
    draggable
    onDragStart={(e) => {
      onDragStart(e);
      e.dataTransfer.effectAllowed = 'move';
    }}
    sx={{ display: 'inline-flex', cursor: 'grab', '&:active': { cursor: 'grabbing' }, ...sx }}
  >
    {children}
  </Box>
);

interface WorkflowPaletteProps {
  /** Click-to-add a raw primitive — unchanged behavior (ref: 04_...md §2). */
  onAdd: (type: PrimitiveType) => void;
  /** NEW — click-to-add a friendly catalog action (ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §3). */
  onAddAction: (action: CatalogAction) => void;
  catalogActions: CatalogActionsByCategory | null;
  catalogLoading: boolean;
  catalogError: string | null;
  /** NEW — which external systems are currently active, keyed by connection_id. Purely
   * informational; a tile whose connection isn't active still works exactly the same —
   * this only adds a heads-up so a user understands why it might fail before adding it
   * (ref: Workflow Canvas — Save API Integration Guide §1). */
  catalogConnectionsById?: Record<string, CatalogConnection>;
}

/**
 * "Add step" palette. Primary tiles are Action Catalog entries, grouped by category
 * (business language — "Create leave request", not "Call API"). Raw primitives remain
 * fully available underneath, collapsed by default, as the "Advanced" escape hatch —
 * their click-to-add behavior is byte-for-byte unchanged from before this catalog existed.
 * Ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §3, §7.
 */
const WorkflowPalette: React.FC<WorkflowPaletteProps> = ({
  onAdd,
  onAddAction,
  catalogActions,
  catalogLoading,
  catalogError,
  catalogConnectionsById = {},
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const categories = catalogActions ? Object.keys(catalogActions) : [];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Add step
      </Typography>

      {catalogLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading actions…
          </Typography>
        </Box>
      )}

      {catalogError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Couldn't load the action catalog ({catalogError}).
          {RAW_PRIMITIVES_SECTION_HIDDEN
            ? ' Nothing else on this canvas is affected — try refreshing to retry.'
            : ' Use "Advanced" below to add raw steps — nothing else on this canvas is affected.'}
        </Alert>
      )}

      {categories.map((category) => (
        <Box key={category} sx={{ mb: 2.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1, display: 'block' }}
          >
            {category}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
            {(catalogActions?.[category] ?? []).map((action) => {
              const connection = action.connection_id ? catalogConnectionsById[action.connection_id] : undefined;
              const connectionInactive = Boolean(connection && connection.status.toLowerCase() !== 'active');
              const tooltipText = connectionInactive
                ? `${action.description ?? ''}\n\n⚠ "${connection!.label}" connection is currently ${connection!.status} — this step may fail until it's reconnected.`
                : action.description ?? '';
              return (
                <DraggableTile
                  key={action.action_id}
                  onDragStart={(e) => e.dataTransfer.setData(DRAG_MIME_ACTION, JSON.stringify(action))}
                >
                  <Tooltip
                    title={<Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block' }}>{tooltipText}</Box>}
                    placement="top"
                    arrow
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onAddAction(action)}
                      startIcon={connectionInactive ? <WarningAmberIcon sx={{ fontSize: 15, color: 'warning.main' }} /> : undefined}
                      sx={{ textTransform: 'none' }}
                    >
                      + {action.label}
                    </Button>
                  </Tooltip>
                </DraggableTile>
              );
            })}
          </Box>
        </Box>
      ))}

      {!RAW_PRIMITIVES_SECTION_HIDDEN && (
        <>
          <Button
            size="small"
            onClick={() => setShowAdvanced((v) => !v)}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', mt: categories.length ? 1 : 0, color: 'text.secondary' }}
          >
            Advanced: raw primitives
          </Button>
          <Collapse in={showAdvanced}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {PRIMITIVE_TYPES.map((type: PrimitiveType) => (
                <DraggableTile key={type} onDragStart={(e) => e.dataTransfer.setData(DRAG_MIME_PRIMITIVE, type)}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onAdd(type)}
                    sx={{
                      textTransform: 'none',
                      borderColor: PRIMITIVE_COLORS[type],
                      color: PRIMITIVE_COLORS[type],
                      '&:hover': { borderColor: PRIMITIVE_COLORS[type], backgroundColor: `${PRIMITIVE_COLORS[type]}11` },
                    }}
                  >
                    + {PRIMITIVE_LABELS[type]}
                  </Button>
                </DraggableTile>
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default WorkflowPalette;
